<script lang="ts" setup>
import { ref, watch } from "vue";
import type { VForm } from "vuetify/components";

import { API } from "@/api/api";
import FacilitySelect from "@/views/orders/components/FacilitySelect.vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import StopDateRange from "@/views/orders/components/StopDateRange.vue";
import { OrderStopState, OrderStopStatus, OrderStopTimeType, OrderStopType } from "@/constants/order";
import { useError } from "@/composables/useError";
import { generateId } from "@/utils/generateId";
import type { TripStop } from "@/types/trip";
import OmniTextarea from "@/components/omni/OmniTextarea.vue";
import { useI18n } from "vue-i18n";

const api = new API();
const { scrollToError } = useError();
const { t } = useI18n();

const showDialog = defineModel<boolean>({ default: false });

withDefaults(
  defineProps<{
    tripStops?: TripStop[];
    position?: number;
  }>(),
  {
    tripStops: () => [],
    position: undefined,
  },
);

const emit = defineEmits<{
  (e: "add", value: TripStop): void;
}>();

const form = ref<VForm | null>(null);

const reloadStop = ref<TripStop>({
  facility: {
    type: "",
    name: "",
    timezone: "",
    addressTimezone: "",
    address: "",
    addressLine: "",
    addressCoordinates: { lat: 0, lon: 0 },
    preciseCoordinates: { lat: 0, lon: 0 },
  },
  freights: [],
  isDeleted: false,
  name: "",
  number: 0,
  position: 0,
  facilityId: 0,
  note: "",
  timeTo: null,
  timeFrom: null,
  timeType: OrderStopTimeType.appointment,
  timezone: null,
  type: OrderStopType.reload,
  address: "",
  orderStopId: null,
  state: OrderStopState.new,
  status: OrderStopStatus.notStarted,
  isReload: true,
  id: generateId(),
});

const loading = ref(false);

watch(showDialog, (value) => {
  if (!value) {
    reloadStop.value.facilityId = 0;
    reloadStop.value.note = "";
  }
});

async function loadFacility() {
  if (!reloadStop.value.facilityId) {
    return;
  }

  loading.value = true;
  try {
    const response = await api.company.findFacilityById(reloadStop.value.facilityId);

    if (!response.data) {
      return;
    }

    reloadStop.value.facility = response.data;
    reloadStop.value.timezone = reloadStop.value.facility.timezone;
    reloadStop.value.address = reloadStop.value.facility.address;
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
}

async function save() {
  const validResult = await form.value!.validate();

  if (!validResult.valid) {
    scrollToError();
    return;
  }

  emit("add", reloadStop.value);
}
</script>
<template>
  <omni-dialog
    v-model="showDialog"
    :loading="loading"
    primary-action-label="Add"
    title="Add reload stop"
    width="830"
    @primary-action:click="save"
  >
    <v-form ref="form" class="py-4">
      <facility-select
        v-model="reloadStop.facilityId"
        :label="t('company.facility')"
        class="mb-3"
        editable
        @update:model-value="loadFacility"
      />

      <omni-textarea
        v-model="reloadStop.note"
        :label="t('trip.reload-stop-note')"
        class="mt-6"
        rows="3"
      ></omni-textarea>

      <div class="font-size-16 text-grey-darken-3 font-weight-500 mt-6">{{ t("general.time") }}</div>

      <div class="mt-4">
        <stop-date-range v-model="reloadStop" hide2-time-frame />
      </div>
    </v-form>
  </omni-dialog>
</template>
